import { MAX_ANIMA } from "./rules";
// ════════════════════════════════════════════════════════════════════
// TERROR — THE SECOND WIND · multi-phase bosses
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-08-18, describing Sir H'osh Vrown:
//   "for the final battle you fight his real Avatar and it's a 2 part
//    battle. He starts again with the same board as when you beat him
//    but the game doesn't end, he refreshes Anima and Khao, no more
//    Avatar, it's the real SHV — so you must beat both in a row.
//    1 duel, 1 node, 1 interaction, boom 2 phase boss >:3"
//
// ── WHAT MAKES THIS CHEAP ────────────────────────────────────────────
// "The same board as when you beat him" is not a feature to build — it
// is what happens if you simply DO NOT END THE MATCH. The board is
// already persistent; ending it is the event. So a phase change is a
// *refusal* to end plus two number writes, and every card standing,
// every Tome set, every Necropolis pile and every Soul stays exactly
// where the first phase left it, for free.
//
// ── THE ONE CHOKEPOINT ───────────────────────────────────────────────
// Nineteen places in Shatterfield can end a duel. Rather than teach
// nineteen call sites about phases, the import is renamed to
// `endMatchRaw` and a component-scoped `endMatch` wraps it — so every
// existing call site routes through the check without being touched,
// including the ones nobody has written yet. A rule enforced at one
// chokepoint cannot be forgotten at the twentieth.
//
// ── THE STATE LIVES ON THE MATCH ─────────────────────────────────────
//   matches/{code}/bossPhases : [ phase, phase, ... ]   ← written once
//   matches/{code}/phase      : number (absent = 0)     ← the cursor
//
// On the match rather than in component state, deliberately: it must
// survive a reconnect mid-boss, and in PvP both seats have to agree
// about which phase they are in. A boss whose second wind evaporates
// when somebody's wifi blinks is worse than no second wind.
//
// ── A PHASE SPEC ─────────────────────────────────────────────────────
//   name        — who they become. "no more Avatar, it's the real SHV."
//   title       — the line under the name.
//   say         — what the table hears. One string; the announcement
//                 layer decides how loudly.
//   anima       — "refresh" | number | null(leave). Default "refresh".
//   khao        — "refresh" | number | null(leave). Default "refresh".
//   playerAnima — DIAL, default null (leave the player exactly as the
//                 first phase left them, which is the whole difficulty
//                 of "beat both in a row"). Set it if a fight turns out
//                 to be unfair rather than hard — that is a playtest
//                 answer, not a design one, so it is a field.
//   accentColor — optional; lets a phase change the board's accent.
//
// ⚠ THE FLOOR: a phase NEVER refreshes the player's board or hand.
// Escalation is the boss standing back up, not the duel restarting.
// ════════════════════════════════════════════════════════════════════

export const PHASE_KEY = "phase";

// Which phase index the match is currently in. Absent means 0 — an
// unphased boss and a phase-0 boss are the same thing, which is why
// nothing has to be written when a normal duel starts.
export const phaseIndexOf = (match) => {
  const n = Number(match?.[PHASE_KEY]);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// The phase a defeat would escalate INTO, or null if this is the end.
// Pure. Safe in a render, safe in a handler, safe to call twice.
export function pendingPhase(match, loserIdx) {
  const phases = match?.bossPhases;
  if (!Array.isArray(phases) || !phases.length) return null;
  // Only the boss seat escalates. The player losing is a loss —
  // a second wind for both seats is a duel that cannot be lost.
  const bossIdx = Number.isFinite(Number(match?.bossSeat)) ? Number(match.bossSeat) : 1;
  if (loserIdx !== bossIdx) return null;
  const next = phases[phaseIndexOf(match)];
  return next || null;
}

export const hasMorePhases = (match, loserIdx) => !!pendingPhase(match, loserIdx);

// How many phases remain after the current one — for a HUD that wants
// to show "PHASE 1 OF 2" rather than surprising somebody.
export function phaseLabel(match) {
  const phases = match?.bossPhases;
  if (!Array.isArray(phases) || !phases.length) return null;
  const total = phases.length + 1;              // the opening is phase 1
  return { current: phaseIndexOf(match) + 1, total };
}

const resolve = (spec, current, max) => {
  if (spec === "refresh") return max;
  if (typeof spec === "number" && Number.isFinite(spec)) return Math.max(0, Math.floor(spec));
  return current;
};

// The exact Firebase payload an escalation writes. Returned rather than
// written so this file stays a pure leaf and the caller owns the socket
// — and so the selftest can assert on it without a database.
//
// Keys are match-root paths, so ONE update() lands the whole escalation
// atomically. A boss that gains its Anima and its new name in two
// writes is a boss that can be seen half-escalated.
export function escalationUpdates(match, loserIdx, phase) {
  if (!match || !phase) return null;
  const seat = match.players?.[loserIdx] || {};
  const player = match.players?.[loserIdx === 0 ? 1 : 0] || {};

  const anima = resolve(phase.anima ?? "refresh", seat.anima ?? 0, seat.maxAnima ?? MAX_ANIMA);
  const khao = resolve(phase.khao ?? "refresh", seat.khao ?? 0, seat.maxKhao ?? 3);

  const updates = {
    [PHASE_KEY]: phaseIndexOf(match) + 1,
    [`players/${loserIdx}/anima`]: anima,
    [`players/${loserIdx}/khao`]: khao,
  };
  if (phase.name)  updates[`players/${loserIdx}/name`] = phase.name;
  if (phase.title) updates[`players/${loserIdx}/title`] = phase.title;

  // The player's own Anima is left alone unless a phase explicitly says
  // otherwise — see THE FLOOR above.
  if (phase.playerAnima != null) {
    const pIdx = loserIdx === 0 ? 1 : 0;
    updates[`players/${pIdx}/anima`] =
      resolve(phase.playerAnima, player.anima ?? 0, player.maxAnima ?? 30);
  }
  return updates;
}

// ── AUTHORING HELP ───────────────────────────────────────────────────
// A boss is "two-phase" by carrying ONE entry here: the opening state
// is the boss as built, and each entry is who they become after being
// put down once. Three entries is a four-phase boss and needs no code.
export const twoPhase = (spec) => [spec];

export function auditPhases(phases) {
  if (!Array.isArray(phases)) return { ok: true, issues: [] };
  const issues = [];
  phases.forEach((p, i) => {
    if (!p || typeof p !== "object") { issues.push(`phase ${i}: not an object`); return; }
    if (!p.name) issues.push(`phase ${i}: no name — the table will not know who stood back up`);
    for (const k of ["anima", "khao", "playerAnima"]) {
      const v = p[k];
      if (v == null || v === "refresh") continue;
      if (typeof v !== "number" || !Number.isFinite(v)) issues.push(`phase ${i}: ${k} is neither "refresh", a number, nor null`);
    }
  });
  return { ok: !issues.length, issues };
}

const bossPhases = {
  PHASE_KEY, phaseIndexOf, pendingPhase, hasMorePhases, phaseLabel,
  escalationUpdates, twoPhase, auditPhases,
};
export default bossPhases;
